import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchIcon from "@mui/icons-material/Search";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import ApiService from "../../../services/categoryManagementApi";

const { reportsAndAnalyticsApi } = ApiService;

// ---- UTC helpers ----
function toUTCStart(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

const SalesTrendsReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportGenerated, setReportGenerated] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [search, setSearch] = useState("");

  // ---- Validation
  const isDateRangeValid =
    fromDate &&
    toDate &&
    !isNaN(new Date(fromDate)) &&
    !isNaN(new Date(toDate)) &&
    toDate >= fromDate;

  const fetchReport = async () => {
    if (!isDateRangeValid) {
      toast.error("Please select a valid date range.");
      return;
    }
    setLoading(true);
    try {
      const response = await reportsAndAnalyticsApi.getUserReport({
        type: "salesTrends",
        startDate: toUTCStart(new Date(fromDate)).toISOString(),
        endDate: toUTCEnd(new Date(toDate)).toISOString(),
        search,
      });
      setReportData(response?.data?.data?.result || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch sales trends report");
      console.error("Error fetching sales trends report:", error);
    } finally {
      setLoading(false);
      setReportGenerated(true);
    }
  };

  const handleReset = () => {
    setFromDate(null);
    setToDate(null);
    setSearch("");
    setReportGenerated(false);
    setReportData([]);
  };

  const exportToExcel = () => {
    const exportData = reportData.map((item) => ({
      "Product Name": item.productName || "N/A",
      "Total Quantity": item.totalQty ?? 0,
      "Total Sales": item.totalSales?.toFixed(2) ?? "0.00",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Trends Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `SalesTrendsReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>Sales Trends Report</Typography>
        <Grid container spacing={2} alignItems="center" mb={2}>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={val => {
                setFromDate(val);
                if (toDate && val && toDate < val) setToDate(null);
              }}
              maxDate={toDate || undefined}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={val => setToDate(val)}
              minDate={fromDate || undefined}
              maxDate={new Date()}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchReport}
              disabled={loading || !isDateRangeValid}
              sx={{ height: 48 }}
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              sx={{ height: 48 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Results */}
        {reportData.length > 0 ? (
          <>
            <Button
              variant="contained"
              onClick={exportToExcel}
              sx={{ mb: 2 }}
              disabled={reportData.length === 0}
            >
              Export Excel
            </Button>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product Name</TableCell>
                    <TableCell>Total Quantity</TableCell>
                    <TableCell>Total Sales</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.productName || "N/A"}</TableCell>
                      <TableCell>{item.totalQty ?? 0}</TableCell>
                      <TableCell>{item.totalSales?.toFixed(2) ?? "0.00"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : reportGenerated ? (
          <Typography sx={{ mt: 3 }} color="text.secondary" align="center">
            No data found.
          </Typography>
        ) : (
          <Paper
            elevation={2}
            sx={{
              mt: 4,
              width: "100%",
              minHeight: "35vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fafbfc",
              boxSizing: "border-box",
              p: { xs: 2, sm: 4 },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Select a date range above and click <strong>Generate Report</strong> to see trends.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can export data to Excel once the report is loaded.
            </Typography>
          </Paper>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default SalesTrendsReport;
